import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { useAppSelector } from '../hooks/redux';
import { creatorStreamPath, streamFullscreenPath } from '../utils/publicIds';
import Layout from '../components/layout/Layout';
import MainLayout from '../components/layout/MainLayout';
import AuthLayout from '../components/layout/AuthLayout';
import ProtectedRoute from '../components/auth/ProtectedRoute';
import LoadingSpinner from '../components/common/LoadingSpinner';
import LegacyAuctionRedirect from '../components/common/LegacyAuctionRedirect';

// Lazy load all pages for better performance
const HomePage = lazy(() => import('../pages/HomePage'));
const SimpleLandingPage = lazy(() => import('../pages/SimpleLandingPage'));
const SearchPage = lazy(() => import('../pages/SearchPage'));
const LoginPage = lazy(() => import('../pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('../pages/auth/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('../pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('../pages/auth/ResetPasswordPage'));
const AuctionListPage = lazy(() => import('../pages/auctions/AuctionListPage'));
const AuctionCreatePage = lazy(() => import('../pages/auctions/AuctionCreatePage'));
const AuctionDetailPage = lazy(() => import('../pages/auctions/AuctionDetailPage'));
const MyWinsPage = lazy(() => import('../pages/auctions/MyWinsPage'));
const ModelDeliveriesPage = lazy(() => import('../pages/model/ModelDeliveriesPage'));
const DashboardPage = lazy(() => import('../pages/dashboard/DashboardPage'));
const ReferralPage = lazy(() => import('../pages/dashboard/ReferralPage'));
const ProfilePage = lazy(() => import('../pages/profile/ProfilePage'));
const UserProfilePage = lazy(() => import('../pages/profile/UserProfilePage'));
const WalletPage = lazy(() => import('../pages/wallet'));
const LiveStreamsPage = lazy(() => import('../pages/streaming/LiveStreamsPage'));
const GoLivePage = lazy(() => import('../pages/streaming/GoLivePage'));
const FullscreenStreamPage = lazy(() => import('../pages/streaming/FullscreenStreamPage'));
const CreatorFullscreenStreamPage = lazy(() => import('../pages/streaming/CreatorFullscreenStreamPage'));
const PrivateSessionPage = lazy(() => import('../pages/streaming/PrivateSessionPage'));
const DynamicPage = lazy(() => import('../pages/page/[slug]'));
const NotFoundPage = lazy(() => import('../pages/NotFoundPage'));
const MarketplacePage = lazy(() => import('../pages/otc/MarketplacePage'));
const OfferDetailPage = lazy(() => import('../pages/otc/OfferDetailPage'));
const SellerDashboardPage = lazy(() => import('../pages/otc/SellerDashboardPage'));
const MyTradesPage = lazy(() => import('../pages/otc/MyTradesPage'));
const TradeDetailPage = lazy(() => import('../pages/otc/TradeDetailPage'));
const FeedPage = lazy(() => import('../pages/social/FeedPage').then(m => ({ default: m.FeedPage })));
const FollowersPage = lazy(() => import('../pages/social/FollowersPage').then(m => ({ default: m.FollowersPage })));
const FollowingPage = lazy(() => import('../pages/social/FollowingPage').then(m => ({ default: m.FollowingPage })));
const MessagesPage = lazy(() => import('../pages/social/MessagesPage').then(m => ({ default: m.MessagesPage })));
const UsersPage = lazy(() => import('../pages/social/UsersPage').then(m => ({ default: m.UsersPage })));
const TrendingPage = lazy(() => import('../pages/social/TrendingPage').then(m => ({ default: m.TrendingPage })));
const PostDetailPage = lazy(() => import('../pages/social/PostDetailPage').then(m => ({ default: m.PostDetailPage })));
const BlockedUsersPage = lazy(() => import('../pages/social/BlockedUsersPage').then(m => ({ default: m.BlockedUsersPage })));
const HelpCenterPage = lazy(() => import('../pages/support/HelpCenterPage'));
const FAQPage = lazy(() => import('../pages/support/FAQPage'));
const ContactPage = lazy(() => import('../pages/support/ContactPage'));
const MyTicketsPage = lazy(() => import('../pages/support/MyTicketsPage'));
const TicketDetailPage = lazy(() => import('../pages/support/TicketDetailPage'));
const SubscriptionPlansPage = lazy(() => import('../pages/subscriptions/SubscriptionPlansPage'));
const MySubscriptionsPage = lazy(() => import('../pages/subscriptions/MySubscriptionsPage'));
const MySubscribersPage = lazy(() => import('../pages/subscriptions/MySubscribersPage'));
const SubscriptionEarningsPage = lazy(() => import('../pages/subscriptions/SubscriptionEarningsPage'));
const GiftEarningsPage = lazy(() => import('../pages/creator/GiftEarningsPage'));
const LockedContentPage = lazy(() => import('../pages/posts/LockedContentPage'));
const GamesPage = lazy(() => import('../pages/games/GamesPage'));
const StreamArchivesListPage = lazy(() => import('../pages/streaming/StreamArchivesListPage'));
const StreamArchivePage = lazy(() => import('../pages/streaming/StreamArchivePage'));
const NotificationsPage = lazy(() => import('../pages/notifications/NotificationsPage'));
const MobileCheckoutReturnPage = lazy(() => import('../pages/checkout/MobileCheckoutReturnPage'));

// Manager Pages
const ManagerDashboardPage = lazy(() => import('../pages/manager/ManagerDashboardPage'));
const CreateCreatorPage = lazy(() => import('../pages/manager/CreateCreatorPage'));
const CreatorDetailPage = lazy(() => import('../pages/manager/CreatorDetailPage'));

// Ad Campaign Pages
const MyCampaignsPage = lazy(() => import('../pages/ads/MyCampaignsPage'));
const CreateCampaignPage = lazy(() => import('../pages/ads/CreateCampaignPage'));
const AdWalletPage = lazy(() => import('../pages/ads/AdWalletPage'));

// Page loading fallback
const PageLoader = () => (
  <div className="min-h-[50vh] flex items-center justify-center">
    <LoadingSpinner size="md" color="primary" message="Loading..." />
  </div>
);

// Redirect authenticated users away from auth pages
const AuthRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAppSelector((state) => state.auth);

  if (isAuthenticated) {
    return <Navigate to="/feed" replace />;
  }

  return <>{children}</>;
};

// Redirect old stream routes to fullscreen versions
const StreamRedirect: React.FC<{ type: 'viewer' | 'creator' }> = ({ type }) => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const targetPath = type === 'creator'
    ? creatorStreamPath(sessionId)
    : streamFullscreenPath(sessionId);
  return <Navigate to={targetPath} replace />;
};

const AppRoutes: React.FC = () => {
  const { isAuthenticated } = useAppSelector((state) => state.auth);

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Landing Page - public website layout */}
        <Route path="/" element={<Layout />}>
          <Route index element={isAuthenticated ? <Navigate to="/feed" replace /> : <SimpleLandingPage />} />
          <Route path="apply-model" element={<Navigate to="/register" replace />} />
          <Route path="apply-manager" element={<Navigate to="/register" replace />} />
        </Route>

        {/* Auth Routes - app-style shell, no website header/footer */}
        <Route path="/" element={<AuthLayout />}>
          <Route path="login" element={<AuthRoute><LoginPage /></AuthRoute>} />
          <Route path="register" element={<AuthRoute><RegisterPage /></AuthRoute>} />
          <Route path="forgot-password" element={<ForgotPasswordPage />} />
          <Route path="reset-password" element={<ResetPasswordPage />} />
        </Route>

        {/* Fullscreen Stream Routes - No header/footer, truly fullscreen */}
        <Route path="checkout/mobile-return" element={<MobileCheckoutReturnPage />} />
        <Route path="stream/fullscreen/:sessionId" element={<ProtectedRoute><FullscreenStreamPage /></ProtectedRoute>} />
        <Route path="stream/creator/fullscreen/:sessionId" element={<ProtectedRoute><CreatorFullscreenStreamPage /></ProtectedRoute>} />
        <Route path="private-session/:requestId" element={<ProtectedRoute><PrivateSessionPage /></ProtectedRoute>} />

        {/* Main App Routes - Use MainLayout with sidebars for authenticated users */}
        <Route path="/" element={<MainLayout />}>
          <Route path="home" element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
          <Route path="search" element={<ProtectedRoute><SearchPage /></ProtectedRoute>} />

          {/* Social Feed - Main landing for authenticated users */}
          <Route path="feed" element={<ProtectedRoute><FeedPage /></ProtectedRoute>} />
          <Route path="post/:postId" element={<ProtectedRoute><PostDetailPage /></ProtectedRoute>} />
          <Route path="users" element={<ProtectedRoute><UsersPage /></ProtectedRoute>} />
          <Route path="trending" element={<ProtectedRoute><TrendingPage /></ProtectedRoute>} />
          <Route path="users/:userId/followers" element={<ProtectedRoute><FollowersPage /></ProtectedRoute>} />
          <Route path="users/:userId/following" element={<ProtectedRoute><FollowingPage /></ProtectedRoute>} />
          <Route path="messages" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
          <Route path="messages/:conversationId" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
          <Route path="blocked-users" element={<ProtectedRoute><BlockedUsersPage /></ProtectedRoute>} />

          {/* Game-a-Rama - Login required to view or bid */}
          <Route path="game-a-rama" element={<ProtectedRoute><AuctionListPage /></ProtectedRoute>} />
          <Route path="game-a-rama/create" element={<ProtectedRoute><AuctionCreatePage /></ProtectedRoute>} />
          <Route path="game-a-rama/:id" element={<ProtectedRoute><AuctionDetailPage /></ProtectedRoute>} />
          <Route path="game-a-rama/my-wins" element={<ProtectedRoute><MyWinsPage /></ProtectedRoute>} />
          <Route path="auctions/*" element={<LegacyAuctionRedirect />} />
          <Route path="my-wins" element={<LegacyAuctionRedirect />} />
          <Route path="deliveries" element={<ProtectedRoute><ModelDeliveriesPage /></ProtectedRoute>} />

          {/* Streaming */}
          <Route path="streams" element={<ProtectedRoute><LiveStreamsPage /></ProtectedRoute>} />
          <Route path="games" element={<ProtectedRoute><GamesPage /></ProtectedRoute>} />
          <Route path="stream/:sessionId" element={<ProtectedRoute><StreamRedirect type="viewer" /></ProtectedRoute>} />
          <Route path="go-live" element={<ProtectedRoute><GoLivePage /></ProtectedRoute>} />
          <Route path="stream/creator/:sessionId" element={<ProtectedRoute><StreamRedirect type="creator" /></ProtectedRoute>} />
          <Route path="watch/:creatorId" element={<ProtectedRoute><FullscreenStreamPage /></ProtectedRoute>} />
          <Route path="streaming/archives" element={<ProtectedRoute><StreamArchivesListPage /></ProtectedRoute>} />
          <Route path="streaming/archive/:streamId" element={<ProtectedRoute><StreamArchivePage /></ProtectedRoute>} />

          {/* Subscriptions */}
          <Route path="subscriptions" element={<ProtectedRoute><MySubscriptionsPage /></ProtectedRoute>} />
          <Route path="subscriptions/plans" element={<ProtectedRoute><MySubscriptionsPage /></ProtectedRoute>} />
          <Route path="subscriptions/manage" element={<ProtectedRoute><SubscriptionPlansPage /></ProtectedRoute>} />
          <Route path="subscriptions/subscribers" element={<ProtectedRoute><MySubscribersPage /></ProtectedRoute>} />
          <Route path="subscriptions/earnings" element={<ProtectedRoute><SubscriptionEarningsPage /></ProtectedRoute>} />
          <Route path="creator/gifts" element={<ProtectedRoute><GiftEarningsPage /></ProtectedRoute>} />

          {/* Locked Content */}
          <Route path="posts/locked" element={<ProtectedRoute><LockedContentPage /></ProtectedRoute>} />

          {/* OTC Routes */}
          <Route path="otc" element={<ProtectedRoute><MarketplacePage /></ProtectedRoute>} />
          <Route path="otc/offers/:id" element={<ProtectedRoute><OfferDetailPage /></ProtectedRoute>} />
          <Route path="otc/apply-seller" element={<Navigate to="/otc/seller/dashboard" replace />} />
          <Route path="otc/seller/dashboard" element={<ProtectedRoute><SellerDashboardPage /></ProtectedRoute>} />
          <Route path="otc/my-trades" element={<ProtectedRoute><MyTradesPage /></ProtectedRoute>} />
          <Route path="otc/trades/:id" element={<ProtectedRoute><TradeDetailPage /></ProtectedRoute>} />

          {/* Support Pages - public */}
          <Route path="help" element={<HelpCenterPage />} />
          <Route path="faq" element={<FAQPage />} />
          <Route path="contact" element={<ContactPage />} />
          <Route path="support/tickets" element={<ProtectedRoute><MyTicketsPage /></ProtectedRoute>} />
          <Route path="support/tickets/:id" element={<ProtectedRoute><TicketDetailPage /></ProtectedRoute>} />

          {/* CMS Pages - public */}
          <Route path="page/:slug" element={<DynamicPage />} />
          {/* Convenience redirects → CMS slugs */}
          <Route path="terms" element={<Navigate to="/page/terms-of-service" replace />} />
          <Route path="privacy" element={<Navigate to="/page/privacy-policy" replace />} />
          <Route path="cookies" element={<Navigate to="/page/cookie-policy" replace />} />
          <Route path="safety" element={<Navigate to="/page/safety-guidelines" replace />} />
          <Route path="compliance" element={<Navigate to="/page/compliance" replace />} />
          <Route path="account-deletion" element={<Navigate to="/page/account-deletion" replace />} />

          {/* User Routes */}
          <Route path="dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path="dashboard/referrals" element={<ProtectedRoute><ReferralPage /></ProtectedRoute>} />
          <Route path="profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
          <Route path="profile/:username" element={<ProtectedRoute><UserProfilePage /></ProtectedRoute>} />
          <Route path="wallet" element={<ProtectedRoute><WalletPage /></ProtectedRoute>} />
          <Route path="notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />

          {/* Manager Routes */}
          <Route path="manager/dashboard" element={<ProtectedRoute><ManagerDashboardPage /></ProtectedRoute>} />
          <Route path="manager/creators/new" element={<ProtectedRoute><CreateCreatorPage /></ProtectedRoute>} />
          <Route path="manager/creators/:creatorId" element={<ProtectedRoute><CreatorDetailPage /></ProtectedRoute>} />

          {/* Ad Campaigns */}
          <Route path="ads" element={<ProtectedRoute><MyCampaignsPage /></ProtectedRoute>} />
          <Route path="ads/create" element={<ProtectedRoute><CreateCampaignPage /></ProtectedRoute>} />
          <Route path="ads/wallet" element={<ProtectedRoute><AdWalletPage /></ProtectedRoute>} />

          {/* Unauthorized */}
          <Route path="unauthorized" element={
            <div className="container-custom py-12">
              <div className="max-w-md mx-auto text-center card">
                <svg className="mx-auto h-12 w-12 text-red-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Access Denied</h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">You don't have permission to access this page.</p>
                <button onClick={() => window.history.back()} className="btn-primary">Go Back</button>
              </div>
            </div>
          } />

          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
